/**
 * @file api-bins.ts
 * @description Admin API-клиент ячеек адресного хранения (phase19.14, W14).
 *
 * Зеркалит `apps/api/src/wms/bins/bins.controller.ts`. Ячейка отвечает на один
 * вопрос — «куда идти за партией»: количеств по ячейкам нет, содержимое
 * считается по остатку партии на складе ячейки.
 *
 * Коды ошибок сервера, которые разбирает UI:
 *  - 409 `bin_code_taken` — адрес на складе уже заведён;
 *  - 409 `bin_not_empty` — в ячейке есть партии с остатком (архивация);
 *  - 422 `bin_code_invalid` — формат адреса;
 *  - 422 `bin_not_supported_for_warehouse_kind` — у транзита/ПВЗ полок нет;
 *  - 422 `bin_archived` / `bin_warehouse_mismatch` — перестановка партии.
 */

import { apiFetch } from "./api-client";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Сводка содержимого ячейки (phase19.23 этап A, зеркало `bin-summary.ts`).
 * Карточка ячейки обязана отвечать «пусто или что лежит» до открытия панели.
 */
export interface BinSummaryDto {
  /** Партий с ненулевым остатком. */
  lotsCount: number;
  /** Различных фасовок среди этих партий. */
  skuCount: number;
  /** Суммарный физический остаток (строка — Decimal без потерь). */
  totalQuantity: string;
  /** До трёх артикулов по убыванию количества — подпись на карточке. */
  skuCodes: string[];
  /** true — в ячейку можно класть товар без предупреждения о переносе. */
  isEmpty: boolean;
}

/** Ячейка склада: адрес `A-02-03` + порядок обхода. */
export interface StorageBinDto {
  id: string;
  shopId: string;
  warehouseId: string;
  code: string;
  rack: string;
  shelf: number;
  cell: number;
  sortKey: number;
  label: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Сводка содержимого. Опциональна: старый сервер её не отдаёт, UI тогда
   * показывает «—» вместо «2 фасовки · 18 шт».
   */
  summary?: BinSummaryDto;
}

/** Строка экрана «Содержимое ячейки». */
export interface BinContentRowDto {
  lotId: string;
  batchNumber: string;
  expiresAt: string | null;
  sku: {
    id: string;
    code: string;
    displayName: string | null;
    productName: string;
  };
  quantity: string;
  reserved: string;
  available: string;
}

/** Итог генератора стеллажа: что создастся (dryRun) и что уже есть. */
export interface GenerateBinsResultDto {
  dryRun: boolean;
  created: string[];
  skipped: string[];
  bins: StorageBinDto[];
}

/** Ответ перестановки партии между ячейками. */
export interface MoveLotBinResultDto {
  lotId: string;
  binId: string | null;
  binCode: string | null;
  /**
   * Номер документа «Перемещение», созданного быстрым действием (phase19.23
   * этап B): перестановка теперь оформляется однострочным `MV-…`. `null` —
   * партия уже лежала в целевой ячейке, документ не понадобился, и в тосте
   * номер показывать нечего.
   */
  transferNumber?: string | null;
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const binsApi = {
  /**
   * Справочник ячеек. `withSummary` не передаём без нужды — сводку сервер
   * считает по умолчанию (её ждёт карточка ячейки). Диалогам выбора ячейки
   * сводка не нужна, они шлют `withSummary: false` и экономят запрос к
   * остаткам.
   */
  list: (params?: {
    warehouseId?: string;
    includeArchived?: boolean;
    withSummary?: boolean;
  }) => {
    const q = new URLSearchParams();
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.includeArchived) q.set("includeArchived", "true");
    if (params?.withSummary === false) q.set("withSummary", "false");
    const qs = q.toString();
    return apiFetch<StorageBinDto[]>(`/admin/wms/bins${qs ? `?${qs}` : ""}`);
  },
  findOne: (id: string) => apiFetch<StorageBinDto>(`/admin/wms/bins/${id}`),
  contents: (id: string) =>
    apiFetch<BinContentRowDto[]>(`/admin/wms/bins/${id}/contents`),
  create: (input: {
    warehouseId: string;
    code: string;
    label?: string;
    sortKey?: number;
  }) => apiFetch<StorageBinDto>("/admin/wms/bins", { method: "POST", json: input }),
  update: (
    id: string,
    input: Partial<{ label: string; sortKey: number; isActive: boolean }>,
  ) =>
    apiFetch<StorageBinDto>(`/admin/wms/bins/${id}`, {
      method: "PATCH",
      json: input,
    }),
  /** `dryRun: true` — превью диапазона без записи (форма генератора). */
  generate: (input: {
    warehouseId: string;
    rack: string;
    shelves: number;
    cells: number;
    dryRun?: boolean;
  }) =>
    apiFetch<GenerateBinsResultDto>("/admin/wms/bins/generate", {
      method: "POST",
      json: input,
    }),
  /** «Удаление» = архивация: код ячейки не переиспользуется. */
  archive: (id: string) =>
    apiFetch<StorageBinDto>(`/admin/wms/bins/${id}`, { method: "DELETE" }),
  /** Переставить партию в другую ячейку; `null` — снять размещение. */
  moveLot: (lotId: string, binId: string | null) =>
    apiFetch<MoveLotBinResultDto>(`/admin/wms/lots/${lotId}/move-bin`, {
      method: "POST",
      json: { binId },
    }),
};

// ─── Хелперы формата адреса (зеркало `bin-code.ts`) ───────────────────────────

/** Префикс штрихкода ячейки — отличает скан места от артикула. */
export const BIN_SCAN_PREFIX = "LOC:";

/** Верхняя граница полки и ячейки (формула sortKey держит два разряда). */
export const MAX_BIN_SEGMENT = 99;

const BIN_CODE_RE = /^([A-Za-z])-(\d{1,2})-(\d{1,2})$/;

/**
 * Проверка формата адреса до отправки на сервер: `A-02-03`.
 * Валидация дублируется намеренно — кладовщик должен видеть ошибку сразу,
 * а не после round-trip. Источник правды остаётся на сервере.
 */
export function isValidBinCode(raw: string): boolean {
  const match = BIN_CODE_RE.exec(raw.trim().toUpperCase());
  if (!match) return false;
  const shelf = Number(match[2]);
  const cell = Number(match[3]);
  return (
    shelf >= 1 && shelf <= MAX_BIN_SEGMENT && cell >= 1 && cell <= MAX_BIN_SEGMENT
  );
}

/**
 * Скан места → код ячейки: `LOC:a-02-03` → `A-02-03` (phase19.24 этап 1,
 * шаг 4).
 *
 * Поле ячейки везде принимает и скан, и ручной ввод: USB-сканер печатает код с
 * префиксом `LOC:`, кладовщик набирает адрес руками. Разбор один на все
 * экраны — сборка зовёт его же через `parsePickingInput`, иначе префикс
 * пришлось бы срезать в каждом новом поле заново.
 */
export function normalizeBinScan(raw: string): string {
  const value = raw.trim();
  const upper = value.toUpperCase();
  return upper.startsWith(BIN_SCAN_PREFIX)
    ? upper.slice(BIN_SCAN_PREFIX.length).trim()
    : value;
}

/** Человекочитаемая подпись: «Стеллаж A, полка 2, ячейка 3». */
export function formatBinHuman(bin: StorageBinDto): string {
  return bin.label ?? `Стеллаж ${bin.rack}, полка ${bin.shelf}, ячейка ${bin.cell}`;
}
